'use client';

import { useEffect, useState } from 'react';
import { getTokenStatus } from '@/lib/auth/token-validation';

interface TokenStatusMonitorProps {
  showDebug?: boolean;
}

export default function TokenStatusMonitor({ showDebug = false }: TokenStatusMonitorProps) {
  const [tokenStatus, setTokenStatus] = useState(() => {
    // 클라이언트 사이드에서만 초기 상태 설정
    if (typeof window !== 'undefined') {
      return getTokenStatus();
    }
    return {
      hasToken: false,
      isExpired: false,
      isExpiringSoon: false,
      timeUntilExpiry: null,
      minutesUntilExpiry: null
    };
  });
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // 클라이언트 사이드에서만 실행
    if (typeof window === 'undefined') {
      return;
    }

    // 개발 환경에서만 토큰 상태 모니터링 활성화
    if (!showDebug || process.env.NODE_ENV !== 'development') {
      return;
    }

    const updateStatus = () => {
      setTokenStatus(getTokenStatus());
    };

    // 초기 상태 설정
    updateStatus();

    // 30초마다 상태 업데이트
    const interval = setInterval(updateStatus, 30000);

    // 키보드 단축키로 토글 (Ctrl+Shift+T)
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'T') {
        setIsVisible(prev => !prev);
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      clearInterval(interval);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showDebug]);

  if (!showDebug || process.env.NODE_ENV !== 'development' || !isVisible) {
    return null;
  }

  const getStatusColor = () => {
    if (!tokenStatus.hasToken) return 'text-gray-500';
    if (tokenStatus.isExpired) return 'text-red-500';
    if (tokenStatus.isExpiringSoon) return 'text-yellow-500';
    return 'text-green-500';
  };

  const getStatusText = () => {
    if (!tokenStatus.hasToken) return 'No Token';
    if (tokenStatus.isExpired) return 'Expired';
    if (tokenStatus.isExpiringSoon) return 'Expiring Soon';
    return 'Valid';
  };

  return (
    <div className="fixed bottom-4 right-4 bg-white border border-gray-200 rounded-lg shadow-lg p-4 z-50 max-w-sm">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-gray-900">Token Status Monitor</h3>
        <button
          onClick={() => setIsVisible(false)}
          className="text-gray-400 hover:text-gray-600"
        >
          ×
        </button>
      </div>
      
      <div className="space-y-2 text-xs">
        <div className="flex justify-between">
          <span className="text-gray-600">Status:</span>
          <span className={`font-medium ${getStatusColor()}`}>
            {getStatusText()}
          </span>
        </div>
        
        {tokenStatus.hasToken && tokenStatus.minutesUntilExpiry !== null && (
          <div className="flex justify-between">
            <span className="text-gray-600">Time until expiry:</span>
            <span className={`font-medium ${getStatusColor()}`}>
              {tokenStatus.minutesUntilExpiry} minutes
            </span>
          </div>
        )}
        
        {tokenStatus.hasToken && tokenStatus.timeUntilExpiry !== null && (
          <div className="flex justify-between">
            <span className="text-gray-600">Exact time:</span>
            <span className="font-mono text-gray-700">
              {Math.floor(tokenStatus.timeUntilExpiry / 1000)}s
            </span>
          </div>
        )}
        
        <div className="flex justify-between">
          <span className="text-gray-600">Has Token:</span>
          <span className={tokenStatus.hasToken ? 'text-green-500' : 'text-red-500'}>
            {tokenStatus.hasToken ? 'Yes' : 'No'}
          </span>
        </div>
        
        <div className="flex justify-between">
          <span className="text-gray-600">Is Expired:</span>
          <span className={tokenStatus.isExpired ? 'text-red-500' : 'text-green-500'}>
            {tokenStatus.isExpired ? 'Yes' : 'No'}
          </span>
        </div>
        
        <div className="flex justify-between">
          <span className="text-gray-600">Expiring Soon:</span>
          <span className={tokenStatus.isExpiringSoon ? 'text-yellow-500' : 'text-green-500'}>
            {tokenStatus.isExpiringSoon ? 'Yes' : 'No'}
          </span>
        </div>
      </div>
      
      <div className="mt-3 pt-2 border-t border-gray-200">
        <p className="text-xs text-gray-500">
          Press Ctrl+Shift+T to toggle this monitor
        </p>
      </div>
    </div>
  );
} 